import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

import Button from '../../../components/ui/Button';

const FuelConsumptionChart = ({ vessel, fuelData }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [fuelType, setFuelType] = useState('all');

  const timeRanges = [
    { value: '24h', label: '24 Hours' },
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' }
  ];

  const fuelTypes = [
    { value: 'all', label: 'All Fuels' },
    { value: 'mgo', label: 'MGO' },
    { value: 'hfo', label: 'HFO' },
    { value: 'vlsfo', label: 'VLSFO' }
  ];

  const chartData = fuelData?.map(item => ({
    time: item?.timestamp,
    actual: item?.actualConsumption,
    predicted: item?.predictedConsumption,
    optimal: item?.optimalConsumption,
    speed: item?.speed
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-maritime-md">
          <p className="text-sm font-medium text-foreground mb-2">
            {new Date(label)?.toLocaleString()}
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry?.color }}
              ></div>
              <span className="text-muted-foreground">{entry?.name}:</span>
              <span className="font-medium text-foreground">
                {entry?.value?.toFixed(2)} MT/day
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card rounded-lg border p-6 shadow-maritime-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Fuel Consumption Analysis</h3>
          <p className="text-sm text-muted-foreground">Real-time vs predicted consumption patterns</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {timeRanges?.map((range) => (
              <Button
                key={range?.value}
                variant={timeRange === range?.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setTimeRange(range?.value)}
              >
                {range?.label}
              </Button>
            ))}
          </div>
          
          <div className="w-px h-6 bg-border"></div>
          
          <select
            value={fuelType}
            onChange={(e) => setFuelType(e?.target?.value)}
            className="text-sm border border-border rounded-md px-3 py-1 bg-background"
          >
            {fuelTypes?.map((type) => (
              <option key={type?.value} value={type?.value}>
                {type?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => new Date(value)?.toLocaleDateString()}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              label={{ value: 'Consumption (MT/day)', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="actual"
              stroke="var(--color-primary)"
              strokeWidth={2}
              name="Actual Consumption"
              dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="predicted"
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeDasharray="5 5"
              name="Predicted Consumption"
              dot={{ fill: 'var(--color-accent)', strokeWidth: 2, r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="optimal"
              stroke="var(--color-success)"
              strokeWidth={2}
              name="Optimal Consumption"
              dot={{ fill: 'var(--color-success)', strokeWidth: 2, r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-primary rounded-full"></div>
            <span className="text-sm font-medium text-foreground">Current Consumption</span>
          </div>
          <div className="text-2xl font-bold text-primary">
            {fuelData?.[fuelData?.length - 1]?.actualConsumption?.toFixed(1)} MT/day
          </div>
        </div>
        
        <div className="p-4 bg-success/5 rounded-lg border border-success/20">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-success rounded-full"></div>
            <span className="text-sm font-medium text-foreground">Efficiency Gain</span>
          </div>
          <div className="text-2xl font-bold text-success">
            -2.3%
          </div>
        </div>
        
        <div className="p-4 bg-accent/5 rounded-lg border border-accent/20">
          <div className="flex items-center space-x-2 mb-2">
            <div className="w-3 h-3 bg-accent rounded-full"></div>
            <span className="text-sm font-medium text-foreground">Potential Savings</span>
          </div>
          <div className="text-2xl font-bold text-accent">
            $12,450
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelConsumptionChart;